import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { DjayAvatar } from '../../mascot';
import type { DjayEmotion, DjayVariant } from '../../mascot';
import { MissionBriefing } from '../MissionBriefing';
interface LabHeroProps {
  labWelcome: {
    text: string;
    emotion: DjayEmotion;
  };
  selectedVariant: DjayVariant;
  allModesLength: number;
  onModeClick: (modeId: string, familyId?: string) => void;
}
/**
 * LabHero — Compact welcome surface for the Learning Lab.
 *
 * Combines Djay's contextual greeting with the MissionBriefing recommendation
 * cards. Extracted from LearningLabPage during the Wave C structural refactor
 * (F-CODE-3) — the page itself is now a thin orchestrator.
 *
 * Part of the learningLab domain (DDD co-locatie s57).
 */
export function LabHero({
  labWelcome,
  selectedVariant,
  allModesLength,
  onModeClick
}: LabHeroProps) {
  return <section aria-labelledby="lab-hero-heading" className="rounded-3xl bg-white border border-slate-200 shadow-sm p-6 sm:p-8 space-y-6" data-id="element-3055" data-mp="1eye05fanery2">
      {/* Header row: Djay greeting + lab stat */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-5" data-id="element-3056" data-mp="2zgc6jab15rqb">
        <div className="flex items-start gap-4 flex-1 min-w-0" data-id="element-3057" data-mp="3e3xd5e74692z">
          <div className="flex-shrink-0" data-id="element-3058" data-mp="3e6a2icf7blq2">
            <DjayAvatar size="lg" emotion={labWelcome.emotion} variant={selectedVariant} animate={true} data-id="element-3059" data-mp="39cyuw92kf0x3" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-3060" data-mp="3j3jvu35pohj6">
            <div className="flex items-center gap-2 mb-1.5" data-id="element-3061" data-mp="3j2qn4wn6zhye">
              <Sparkles className="w-4 h-4 text-fuchsia-500" strokeWidth={2.5} data-id="element-3062" data-mp="y6xmlzeli8u" />
              <span className="text-[11px] font-black uppercase tracking-wider text-fuchsia-600" data-id="element-3063" data-mp="3s59o1b7f3ki0">
                Welkom in het Oefenlab
              </span>
            </div>
            <h2 id="lab-hero-heading" className="text-xl sm:text-2xl font-black text-slate-900 leading-tight" data-id="element-3064" data-mp="1xqqmpbym3n1k">
              {labWelcome.text}
            </h2>
          </div>
        </div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.3,
        delay: 0.1
      }} className="flex-shrink-0 self-start sm:self-center rounded-2xl bg-fuchsia-50 border border-fuchsia-100 px-4 py-3 text-center" aria-label={`${allModesLength} oefenmodi beschikbaar`} data-id="element-3065" data-mp="6mfb3ve9h9uv">
          <div className="text-2xl font-black text-fuchsia-700 leading-none" data-id="element-3066" data-mp="ucj3f6m01n6k">
            {allModesLength}
          </div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-fuchsia-600 mt-1" data-id="element-3067" data-mp="yca0tt6171k2">
            manieren
          </div>
        </motion.div>
      </div>

      {/* Mission Briefing — Djay's two recommendations */}
      <div className="pt-2" data-id="element-3068" data-mp="188wik9329chv">
        <MissionBriefing onModeClick={onModeClick} data-id="element-3069" data-mp="1ifphjiy1ba0b" />
      </div>
    </section>;
}